import { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/**
 * App-standard searchable dropdown (Command + Popover combobox), matching the
 * pickers used elsewhere (e.g. ActivityComparison's employee picker).
 *
 * - Type to filter; the list scrolls and shows ~5 rows at a time.
 * - `allowCustom` lets the user commit whatever they typed as a new value
 *   (used by the Add Asset category field, which can add a brand-new category).
 */
export interface SearchableComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  allowCustom?: boolean;
  disabled?: boolean;
  className?: string;
}

export function SearchableCombobox({
  value,
  onChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No match.",
  allowCustom = false,
  disabled = false,
  className,
}: SearchableComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const trimmed = query.trim();
  // Show a "use typed value" affordance only when custom entry is allowed and
  // what they typed isn't already an option (case-insensitive).
  const showCreate =
    allowCustom &&
    !!trimmed &&
    !options.some((o) => o.toLowerCase() === trimmed.toLowerCase());

  const commit = (v: string) => {
    onChange(v);
    setQuery("");
    setOpen(false);
  };

  // cmdk filters by the item `value`; keep its built-in fuzzy match.
  const items = useMemo(() => options, [options]);

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal bg-white",
            !value && "text-gray-400",
            className
          )}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            className="text-sm"
          />
          {/* ~5 rows tall (each ~2.25rem), then scrolls. When this combobox is
              inside a modal Dialog, the Dialog's scroll-lock (react-remove-scroll)
              cancels wheel events that bubble to `document`; stop them here so the
              list scrolls on wheel/touch, not just by dragging the scrollbar. */}
          <CommandList
            className="max-h-[11.25rem]"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <CommandEmpty className="py-3 text-center text-xs text-gray-400">
              {emptyText}
            </CommandEmpty>
            {showCreate && (
              <CommandGroup>
                <CommandItem
                  value={`__create__${trimmed}`}
                  onSelect={() => commit(trimmed)}
                  className="cursor-pointer text-sm"
                >
                  <span className="text-emerald-600">+ Add “{trimmed}”</span>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {items.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => commit(opt)}
                  className="cursor-pointer text-sm"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === opt ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="truncate">{opt}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
